import { Box, Card, TextField, FormControl, Button, Typography, Tooltip, Select, MenuItem, InputAdornment } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { grey } from '@mui/material/colors';
import { useState, useEffect, useMemo } from 'react';
import ExpandMoreIcon from '../../icons/ExpandMoreIcon';
import CheckIcon from '@mui/icons-material/Check';

interface OutpaintBGProps {
  onAction: () => void;
  onAspectRatioChange?: (aspectRatio: string) => void;
  onDimensionsChange?: (dimensions: { width: number; height: number }) => void;
  currentCanvasDimensions?: { width: number; height: number };
  currentGenerationDimensions?: { width: number; height: number };
  canvasWidth?: string
}

const OutpaintBG = ({ onAction, onAspectRatioChange, onDimensionsChange ,canvasWidth}: OutpaintBGProps) => {
  const [aspectRatio, setAspectRatio] = useState('16:9');
  // const BASE_HD_DIMENSION = 1024;
  const [dimensions, setDimensions] = useState({ width: 1920, height: 1080 });

  const aspectRatioOptions = [
    { value: 'original', label: 'Original' },
    { value: 'custom', label: 'Custom' },
    { value: '1:1', label: '1:1' },
    { value: '16:9', label: '16:9' },
    { value: '9:16', label: '9:16' },
    { value: '3:2', label: '3:2' },
    { value: '2:3', label: '2:3' },
    { value: '4:3', label: '4:3' },
    { value: '3:4', label: '3:4' }
  ];


  const resolutionMap = useMemo(
    () => ({
      '1:1': { width: 1920, height: 1920 },
      '16:9': { width: 1920, height: 1080 },
      '9:16': { width: 1080, height: 1920 },
      '3:2': { width: 1920, height: 1280 },
      '2:3': { width: 1280, height: 1920 },
      '4:3': { width: 1920, height: 1440 },
      '3:4': { width: 1440, height: 1920 }
    }),
    []
  );
  const handleAspectRatioChange = (event: any) => {
    const newRatio = event.target.value;
    setAspectRatio(newRatio);
    if (onAspectRatioChange) {
      onAspectRatioChange(newRatio);
    }
  };

  const handleDimensionsChange = (field: 'width' | 'height', value: number) => {
    setAspectRatio('custom');
    const newDimensions = { ...dimensions, [field]: value };
    setDimensions(newDimensions);
    if (onDimensionsChange) {
      onDimensionsChange(newDimensions);
    }
  };

  useEffect(() => {}, [onAspectRatioChange]);
  useEffect(() => {
    const newDimensions = resolutionMap[aspectRatio];
    if (newDimensions) {
      setDimensions(newDimensions);
      if (onDimensionsChange) {
        onDimensionsChange(newDimensions);
      }
    }
  }, [aspectRatio, onDimensionsChange, resolutionMap]);
  return (
    <Card
      sx={{
        width: canvasWidth==="true" ? '286px' :'320px',
        height: '280px',
        backgroundColor: 'rgba(24, 24, 27, 1)',
        pl: 2,
        pr: 1,
        pt: 2,
        pb: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        borderRadius: 2,
        mt: '43px',
        fontFamily: 'Inter'
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Typography color="white" fontWeight="Bold" fontSize={16}>
          Outpaint
        </Typography>

        <Tooltip
          title={
            <Typography fontSize="10px" color="black" width="200px">
              Expand images beyond their borders by specifying aspect ratio of the new output image then resizing and moving the input image within the new
              output image as per your liking.
            </Typography>
          }
          placement="bottom"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: 'white',
                color: 'black',
                borderRadius: 1,
                boxShadow: 1,
                p: 0.5
              }
            }
          }}
        >
          <InfoOutlinedIcon fontSize="small" sx={{ color: grey[400], cursor: 'pointer' }} />
        </Tooltip>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography color="white" fontSize={14} fontWeight={600}>
          Aspect Ratio
        </Typography>

        <Tooltip
          title={
            <Typography fontSize="10px" color="black" width="200px">
              Choose a predefined aspect ratio from the dropdown or select Custom option and specify custom dimensions below.
            </Typography>
          }
          placement="bottom"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: 'white',
                color: 'black',
                borderRadius: 1,
                boxShadow: 1,
                p: 0.5
              }
            }
          }}
        >
          <InfoOutlinedIcon fontSize="small" sx={{ color: grey[400], cursor: 'pointer' }} />
        </Tooltip>
      </Box>
      <Box>
        <FormControl fullWidth>
          <Select
            value={aspectRatio}
            onChange={handleAspectRatioChange}
            IconComponent={ExpandMoreIcon}
            renderValue={selectedValue => aspectRatioOptions.find(option => option.value === selectedValue)?.label}
            sx={{
              width: canvasWidth==="true" ? '100%' :'296px',
              height: '35px',
              backgroundColor: 'rgba(57, 57, 57, 1)',
              borderRadius: '8px',

              color: '#E5E5E5',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '& .MuiSelect-select': {
                padding: '6px 14px',
                fontSize: '14px',
                fontWeight: 400
              },
              '& .MuiSvgIcon-root': {
                color: 'rgba(250, 250, 250, 1)'
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: '#393939',
                  borderRadius: 2,
                  mt: 1,
                  boxShadow: 'none',
                  padding: '4px 12px',
                  fontFamily: 'Inter',
                  '& .MuiMenuItem-root': {
                    color: '##18181B',
                    fontSize: '14px',
                    fontFamily: 'Inter',
                    margin: '4px 0',
                    '&:hover': {
                      backgroundColor: '#18181B',
                      borderRadius: '8px'
                    },
                    // Add a checkmark to the selected item
                    '&.Mui-selected': {
                      backgroundColor: '#18181B',
                      borderRadius: '8px',
                      '&:hover': {
                        borderRadius: '8px',
                        backgroundColor: '#18181B'
                      }
                    }
                  }
                }
              }
            }}
          >
            {aspectRatioOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <span>{option.label}</span>
                  {aspectRatio === option.value && <CheckIcon sx={{ fontSize: '16px' }} />}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box display="flex" alignItems="center" gap={1}>
        <Typography color="white" fontSize={14} fontWeight={600}>
          Image Dimensions
        </Typography>

        <Tooltip
          title={
            <Typography fontSize="10px" color="black" width="200px">
              Shows dimensions for predefined Aspect Ratios, can also be used to input custom dimensions by selecting Custom option from the dropdown above.
            </Typography>
          }
          placement="bottom"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: 'white',
                color: 'black',
                borderRadius: 1,
                boxShadow: 1,
                p: 0.5
              }
            }
          }}
        >
          <InfoOutlinedIcon fontSize="small" sx={{ color: grey[400], cursor: 'pointer' }} />
        </Tooltip>
      </Box>
      <>
        <Box display="flex" gap={2}>
          <TextField
            type="number"
            value={dimensions.width}
            onChange={e => handleDimensionsChange('width', Number(e.target.value))}
            inputProps={{
              min: 1,
              max: 4096,
              step: 1
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ color: '#A1A1A1' }}>W</Typography>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#393939',
                borderRadius: '8px',
                height: '30px',
                width: canvasWidth==="true" ? '100%' :'139px',
                '& fieldset': { border: 'none' }
              },
              '& .MuiInputBase-input': {
                color: '#A1A1A1',
                fontWeight: 500,
                textAlign: 'right',
                paddingRight: '14px'
              },
              '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                '-webkit-appearance': 'none',
                margin: 0
              },
              '& input[type=number]': {
                '-moz-appearance': 'textfield'
              }
            }}
          />
          <TextField
            type="number"
            value={dimensions.height}
            onChange={e => handleDimensionsChange('height', Number(e.target.value))}
            inputProps={{
              min: 1,
              max: 4096,
              step: 1
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ color: '#A1A1A1' }}>H</Typography>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#393939',
                borderRadius: '8px',
                height: '30px',
                width: canvasWidth==="true" ? '100%' :'139px',
                '& fieldset': { border: 'none' }
              },
              '& .MuiInputBase-input': {
                color: '#A1A1A1',
                fontWeight: 500,
                textAlign: 'right',
                paddingRight: '14px'
              },
              '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                '-webkit-appearance': 'none',
                margin: 0
              },
              '& input[type=number]': {
                '-moz-appearance': 'textfield'
              }
            }}
          />{' '}
        </Box>
      </>
      <Box display={'flex'} justifyContent={'flex-start'} width={'100%'} ml={canvasWidth==="true" ? 21 :25.5}>
        <Button
          onClick={onAction}
          sx={{
            maxWidth: '87px',
            maxHeight: '52px',
            height: '29px',
            fontSize: '12px',
            padding: '16px 40px',
            
            width: 'max-content',
            borderRadius: '170px',
            fontWeight: '600',
            background: 'linear-gradient(163deg, rgb(12, 174, 207) -29.1%, rgb(88, 15, 107) 96.45%)',
            color: 'white',
            textTransform: 'none'
          }}
        >
          Outpaint
        </Button>
      </Box>
    </Card>
  );
};

export default OutpaintBG;
